import { useState, useEffect } from 'react';
import { usePOSStore, Product } from '../stores/posStore';
import ProductCard from '../components/ProductCard';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.webwaka.site';

export default function ProductGrid(): JSX.Element {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const addToCart = usePOSStore((state) => state.addToCart);

  useEffect(() => {
    const fetchProducts = async (): Promise<void> => {
      // Try to load from cache first (offline-first)
      const cached = localStorage.getItem('webwaka_products');
      if (cached) {
        setProducts(JSON.parse(cached) as Product[]);
        setLoading(false);
      }

      // Always attempt to fetch fresh data from API
      try {
        const res = await fetch(`${API_URL}/products`, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = (await res.json()) as Product[];
        const productList = Array.isArray(data) ? data : [];
        setProducts(productList);
        localStorage.setItem('webwaka_products', JSON.stringify(productList));
      } catch (err) {
        console.error('Failed to fetch products from API:', err);
        if (!cached) {
          setError('Unable to load products. Please check your connection.');
        }
      } finally {
        setLoading(false);
      }
    };

    void fetchProducts();
  }, []);

  const handleAddToCart = (product: Product): void => {
    addToCart(product, 1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading products...</div>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Products</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
        ))}
      </div>
    </div>
  );
}
